import { authApiV2 } from '../axiosWrapper';
import { IVerifyHumanRequest, IVerifyHumanResponse } from '../types/humanVerification';
import { trackExceptionWithContext } from './application-insights/appInsights';
import { AppInsightsComponents, AppInsightsErrorTypes } from './application-insights/appInsightsConstants';

export class HumanVerificationService {
  private static readonly BASE_PATH = '/security';

  /**
   * Verify human challenge token with the backend
   */
  static async verifyHuman(request: IVerifyHumanRequest): Promise<IVerifyHumanResponse> {
    try {
      const response = await authApiV2.post<IVerifyHumanResponse>(
        `${this.BASE_PATH}/verify-human`,
        request
      );

      return response.data;
    } catch (error: any) {
      // Handle API errors
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // If the API returns a structured error response
        if (errorData.isSuccess === false) {
          trackExceptionWithContext(new Error(errorData.errorMessage || 'Verification API failed'), {
            component: AppInsightsComponents.HUMAN_VERIFICATION_SERVICE,
            action: 'verify-human-api-error',
            additionalData: {
              errorType: AppInsightsErrorTypes.HUMAN_VERIFICATION_ERROR,
              provider: request.provider,
              action: request.action,
              statusCode: error.response?.status,
              tokenLength: request.token?.length || 0
            }
          });
          return errorData as IVerifyHumanResponse;
        }
        
        // If it's a different error format
        const errorMessage = errorData.message || errorData.error || 'Verification failed';
        trackExceptionWithContext(new Error(errorMessage), {
          component: AppInsightsComponents.HUMAN_VERIFICATION_SERVICE,
          action: 'verify-human-unexpected-error',
          additionalData: {
            errorType: AppInsightsErrorTypes.API_ERROR,
            provider: request.provider,
            action: request.action,
            statusCode: error.response?.status,
            responseKeys: Object.keys(errorData)
          }
        });
        throw new Error(errorMessage);
      }
      
      // Handle network/unexpected errors
      const errorMessage = error.message || 'Network error occurred during verification';
      trackExceptionWithContext(error, {
        component: AppInsightsComponents.HUMAN_VERIFICATION_SERVICE,
        action: 'verify-human-network-error',
        additionalData: {
          errorType: AppInsightsErrorTypes.NETWORK_ERROR,
          provider: request.provider,
          action: request.action,
          hasResponse: !!error.response,
          errorCode: error.code
        }
      });
      throw new Error(errorMessage);
    }
  }

  /**
   * Verify Turnstile token
   */
  static async verifyTurnstile(
    token: string,
    action?: string
  ): Promise<IVerifyHumanResponse> {
    return this.verifyHuman({
      token,
      provider: 'turnstile',
      action,
    });
  }

  /**
   * Verify reCAPTCHA token
   */
  static async verifyRecaptcha(
    token: string,
    action?: string,
    minScore?: number
  ): Promise<IVerifyHumanResponse> {
    return this.verifyHuman({
      token,
      provider: 'recaptcha',
      action,
      minScore,
    });
  }
}